import { Box, Tooltip as MuiTooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { createCurrencyFormatter } from "utils/currency-utils";
import { ChartContainer } from "./chart-container";

export interface ChartDataPoint {
  [key: string]: string | number;
}

export interface LegendItem {
  dataKey: string;
  label: string;
  color: string;
  tooltip: ReactNode;
}

export interface ChartSeries {
  dataKey: string;
  label: string;
  fill: string;
  color?: string;
  radius?: [number, number, number, number];
  stackId?: string;
  barSize?: number;
  tooltip: ReactNode;
}

export interface ChartTooltipConfig {
  labelFormatter?: (dataKey: string) => string;
  valueFormatter?: (value: number) => string;
}

export interface UnifiedChartProps {
  title?: string;
  titleTooltip?: ReactNode;
  data: Record<string, Record<string, number>> | Record<string, any>;
  series: ChartSeries[];
  tooltip?: ChartTooltipConfig;
  barCategoryGap?: string;
  xAxisKey?: string;
  xAxisFormatter?: (value: string) => string;
}

const defaultCurrencyFormatter = createCurrencyFormatter("GBP")({
  rounding: {
    kind: "rounded",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
});

const defaultLabelFormatter = (dataKey: string): string => {
  // Convert camelCase to Title Case
  return dataKey
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

export const UnifiedChart = ({
  title,
  titleTooltip,
  data,
  series,
  tooltip = {},
  barCategoryGap = "60%",
  xAxisKey = "month",
  xAxisFormatter = (value) => value,
}: UnifiedChartProps) => {
  // Derive bars from series
  const bars = series.map((s) => ({
    dataKey: s.dataKey,
    fill: s.fill,
    radius: s.radius,
    stackId: s.stackId,
    barSize: s.barSize,
  }));

  // For non-stacked bars, reverse the order so first item renders on top
  const hasStackId = series.some((s) => s.stackId);
  const orderedBars = hasStackId ? bars : bars.reverse();

  // Derive legends from series
  const legends = series.map((s) => ({
    dataKey: s.dataKey,
    label: s.label,
    color: s.color || s.fill,
    tooltip: s.tooltip,
  }));

  // Transform data for recharts
  const chartData = Object.entries(data).map(([key, values]) => ({
    [xAxisKey]: key,
    ...values,
  }));

  const {
    labelFormatter = defaultLabelFormatter,
    valueFormatter = defaultCurrencyFormatter.format,
  } = tooltip;

  // Custom tick formatter for X-axis
  const formatXAxisTick = (value: string) => {
    return xAxisFormatter(value);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // Create a map of dataKey to legend order
      const legendOrder = new Map(
        legends.map((legend, index) => [legend.dataKey, index]),
      );

      // Sort payload based on legend order
      const sortedPayload = [...payload].sort((a, b) => {
        const orderA = legendOrder.get(a.dataKey) ?? Number.MAX_VALUE;
        const orderB = legendOrder.get(b.dataKey) ?? Number.MAX_VALUE;
        return orderA - orderB;
      });

      return (
        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: 1,
            padding: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          {sortedPayload.map((entry: any, index: number) => (
            <Typography
              key={entry.dataKey}
              variant="body2"
              sx={{
                color: "#666",
                fontWeight: 500,
                mb: index === sortedPayload.length - 1 ? 0 : 0.5,
              }}
            >
              {labelFormatter(entry.dataKey)} {valueFormatter(entry.value)}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Tooltip component props for consistent styling
  const tooltipProps = {
    arrow: true,
    placement: "top" as const,
    componentsProps: {
      tooltip: {
        sx: {
          bgcolor: "white",
          color: "#000",
          border: "1px solid #ccc",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "& .MuiTooltip-arrow": {
            color: "white",
            "&::before": {
              border: "1px solid #ccc",
            },
          },
        },
      },
    },
  };

  return (
    <ChartContainer title={title || ""} titleTooltip={titleTooltip}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 10,
                right: 20,
                left: 20,
                bottom: 10,
              }}
              barCategoryGap={barCategoryGap}
            >
              <CartesianGrid strokeDasharray="none" stroke="transparent" />
              <XAxis
                dataKey={xAxisKey}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 14, fill: "#666", fontWeight: 500 }}
                tickFormatter={formatXAxisTick}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              {orderedBars.map((bar) => (
                <Bar
                  key={bar.dataKey}
                  dataKey={bar.dataKey}
                  fill={bar.fill}
                  radius={bar.radius}
                  stackId={bar.stackId}
                  barSize={bar.barSize}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Bottom legend with colored dots */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 4,
            pt: 2,
            pb: 1,
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          {legends.map((legend) => (
            <MuiTooltip
              key={legend.dataKey}
              title={legend.tooltip}
              {...tooltipProps}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: legend.color,
                    borderRadius: "50%",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  {legend.label}
                </Typography>
              </Box>
            </MuiTooltip>
          ))}
        </Box>
      </Box>
    </ChartContainer>
  );
};
